import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Users, ShieldAlert, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { RansomwareItem } from '@/types/ransomware';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";

interface RansomwareGroupsProps {
    data: RansomwareItem[];
}

const ITEMS_PER_PAGE = 12;

export function RansomwareGroups({ data }: RansomwareGroupsProps) {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Aggregate data by group
    const groups = useMemo(() => {
        const groupMap = new Map<string, number>();

        data.forEach(item => {
            if (item.group) {
                const count = groupMap.get(item.group) || 0;
                groupMap.set(item.group, count + 1);
            }
        });

        return Array.from(groupMap.entries())
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count); // Sort by victim count descending
    }, [data]);

    // Filter groups based on search
    const filteredGroups = useMemo(() => {
        if (!searchQuery.trim()) return groups;
        return groups.filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [groups, searchQuery]);

    // Pagination logic
    const totalPages = Math.ceil(filteredGroups.length / ITEMS_PER_PAGE);
    const currentGroups = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredGroups.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredGroups, currentPage]);

    // Reset page when search changes
    useMemo(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handleGroupClick = (groupName: string) => {
        navigate(`/ransomware/group/${encodeURIComponent(groupName)}`);
    };

    // Generate pagination links
    const getPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink
                            onClick={() => setCurrentPage(i)}
                            isActive={currentPage === i}
                        >
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            }
        } else {
            // Always show first page
            items.push(
                <PaginationItem key={1}>
                    <PaginationLink onClick={() => setCurrentPage(1)} isActive={currentPage === 1}>1</PaginationLink>
                </PaginationItem>
            );

            if (currentPage > 3) {
                items.push(<PaginationItem key="ellipsis1"><PaginationEllipsis /></PaginationItem>);
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink onClick={() => setCurrentPage(i)} isActive={currentPage === i}>{i}</PaginationLink>
                    </PaginationItem>
                );
            }

            if (currentPage < totalPages - 2) {
                items.push(<PaginationItem key="ellipsis2"><PaginationEllipsis /></PaginationItem>);
            }

            // Always show last page
            items.push(
                <PaginationItem key={totalPages}>
                    <PaginationLink onClick={() => setCurrentPage(totalPages)} isActive={currentPage === totalPages}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }
        return items;
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('ransomware.searchPlaceholder') || (language === 'en' ? 'Search groups...' : 'Szukaj grup...')}
                        className="pl-10"
                    />
                </div>
                <div className="text-sm text-muted-foreground">
                    {language === 'en' ? 'Total Groups:' : 'Liczba grup:'} {groups.length}
                </div>
            </div>

            {filteredGroups.length === 0 ? (
                <div className="text-center py-20">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-lg text-muted-foreground">
                        {language === 'en' ? 'No groups found' : 'Nie znaleziono grup'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {currentGroups.map((group, index) => (
                            <Card
                                key={group.name}
                                className={cn(
                                    "hover:shadow-lg transition-all duration-300 cursor-pointer group border-l-4 border-l-transparent hover:border-l-primary",
                                    "animate-scale-in"
                                )}
                                style={{ animationDelay: `${Math.min(index * 0.05, 0.5)}s` }}
                                onClick={() => handleGroupClick(group.name)}
                            >
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex justify-between items-start">
                                        <span className="text-lg font-bold truncate mr-2" title={group.name}>
                                            {group.name}
                                        </span>
                                        <ShieldAlert className="h-5 w-5 text-destructive shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                                                {language === 'en' ? 'Victims' : 'Ofiary'}
                                            </p>
                                            <p className="text-2xl font-bold text-primary">
                                                {group.count}
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full group-hover:bg-primary/10 group-hover:text-primary">
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="mt-8">
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                        />
                                    </PaginationItem>

                                    {getPaginationItems()}

                                    <PaginationItem>
                                        <PaginationNext
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                            <div className="text-center text-sm text-muted-foreground mt-2">
                                {language === 'en' ? `Page ${currentPage} of ${totalPages}` : `Strona ${currentPage} z ${totalPages}`}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
